interface ChartDatumsFigureProps {
  /// Max-width in CSS pixels. The SVG fills its container up to
  /// this cap, so on wide screens it expands instead of sitting in
  /// a narrow column with empty space either side.
  size?: number;
}

/// "Which datum is this number measured from?" reference. Every
/// figure printed on an Admiralty chart is referenced to one of two
/// vertical datums: depths and drying heights reference Chart Datum
/// (LAT in UK waters); structure heights, light heights and bridge
/// clearances reference Mean High Water Springs.
///
/// One landscape: an underwater shoal + drying rock on the left,
/// rising onto a coastline with a bridge, a lighthouse and a hill
/// on the right. Each chart annotation is rendered as it appears on
/// paper, with a thin arrow back to the datum line it's measured
/// from. The two datum lines are emphasised so the "above MHWS vs
/// below/above CD" rule reads at a glance.
export function ChartDatumsFigure({ size = 620 }: ChartDatumsFigureProps) {
  // Fixed y coordinates for the two datums and the example tide.
  const Y_HILLTOP = 30;
  const Y_LIGHTHOUSE_TOP = 70;
  const Y_LIGHT = 90;
  const Y_BRIDGE_BOTTOM = 110;
  const Y_MHWS = 170;
  const Y_NOW = 195;
  const Y_CD = 250;
  const Y_DRYING_TOP = 235;
  const Y_SOUNDING_SEABED = 320;

  // X coordinates for each feature column.
  const X_SOUNDING = 110;
  const X_DRYING = 250;
  const X_BRIDGE = 380;
  const X_LIGHTHOUSE = 500;
  const X_HILL = 560;

  // Coastline profile — rises gently from deep water on the left
  // through a drying rock, up a sandy beach, onto rising ground.
  const coastline =
    `M 0 ${Y_SOUNDING_SEABED + 30} ` +
    `L 60 ${Y_SOUNDING_SEABED + 30} ` +
    `C 110 ${Y_SOUNDING_SEABED + 20}, 180 ${Y_SOUNDING_SEABED - 50}, 230 ${Y_SOUNDING_SEABED - 70} ` +
    `C 245 ${Y_DRYING_TOP}, 265 ${Y_DRYING_TOP - 5}, 285 ${Y_SOUNDING_SEABED - 60} ` + // drying rock peak
    `C 310 ${Y_SOUNDING_SEABED - 75}, 340 ${Y_CD - 12}, 360 ${Y_CD - 14} ` +
    `C 400 ${Y_CD - 18}, 440 ${Y_MHWS - 8}, 480 ${Y_MHWS - 22} ` +
    `C 500 ${Y_MHWS - 28}, 530 ${Y_LIGHT - 4}, 560 ${Y_HILLTOP + 6} ` + // hill on the right
    `L 600 ${Y_HILLTOP + 14} ` +
    `L 600 ${Y_SOUNDING_SEABED + 30} Z`;

  return (
    <svg
      viewBox="-60 0 720 360"
      width="100%"
      style={{ display: "block", maxWidth: size, height: "auto" }}
      role="img"
      aria-label="Which datum each chart figure is referenced to: heights above MHWS, depths and drying heights from Chart Datum."
    >
      {/* Sky */}
      <rect x="-60" y="0" width="720" height={Y_NOW} fill="#dbeafe" />
      {/* Sea */}
      <rect
        x="-60"
        y={Y_NOW}
        width="720"
        height={360 - Y_NOW}
        fill="#9cc3e0"
        opacity="0.6"
      />
      {/* Coastline + seabed */}
      <path d={coastline} fill="#caa86b" stroke="#7d6a44" strokeWidth="1" />

      {/* Water surface NOW */}
      <line
        x1="-60"
        y1={Y_NOW}
        x2="660"
        y2={Y_NOW}
        stroke="#1e6091"
        strokeWidth="1.2"
      />
      <text
        x="-54"
        y={Y_NOW - 4}
        fontSize="9"
        fill="#1e6091"
        fontStyle="italic"
        fontFamily="ui-sans-serif, system-ui"
      >
        water surface now
      </text>

      {/* MHWS reference line — emphasised */}
      <line
        x1="-60"
        y1={Y_MHWS}
        x2="660"
        y2={Y_MHWS}
        stroke="#b45309"
        strokeWidth="1.4"
        strokeDasharray="6 4"
      />
      <rect x="-54" y={Y_MHWS - 18} width="180" height="16" fill="#fef3c7" />
      <text
        x="-48"
        y={Y_MHWS - 6}
        fontSize="11"
        fontWeight="700"
        fill="#92400e"
        fontFamily="ui-sans-serif, system-ui"
      >
        MHWS — heights datum
      </text>

      {/* Chart Datum reference line — emphasised */}
      <line
        x1="-60"
        y1={Y_CD}
        x2="660"
        y2={Y_CD}
        stroke="#0f172a"
        strokeWidth="1.6"
      />
      <line
        x1="-60"
        y1={Y_CD + 2.5}
        x2="660"
        y2={Y_CD + 2.5}
        stroke="#0f172a"
        strokeWidth="1.6"
      />
      <rect x="-54" y={Y_CD + 4} width="240" height="16" fill="#e0f2fe" />
      <text
        x="-48"
        y={Y_CD + 16}
        fontSize="11"
        fontWeight="700"
        fill="#0c4a6e"
        fontFamily="ui-sans-serif, system-ui"
      >
        Chart Datum (LAT) — depths datum
      </text>

      {/* ── Features ──────────────────────────────────────────── */}

      {/* Bridge — flat deck spanning the channel, with piers going
          DOWN from the deck to where they land on the coastline.
          Left pier descends through the water column onto the
          rising seabed; right pier lands higher on the slope. */}
      <rect
        x={X_BRIDGE - 72}
        y={Y_BRIDGE_BOTTOM - 4}
        width="144"
        height="6"
        fill="#1c2530"
      />
      {/* Left pier — into deep water, lands on the underwater
          coastline around y=240. */}
      <rect
        x={X_BRIDGE - 66}
        y={Y_BRIDGE_BOTTOM + 2}
        width="6"
        height={245 - Y_BRIDGE_BOTTOM}
        fill="#1c2530"
      />
      {/* Right pier — lands on the rising land around y=175. */}
      <rect
        x={X_BRIDGE + 60}
        y={Y_BRIDGE_BOTTOM + 2}
        width="6"
        height={175 - Y_BRIDGE_BOTTOM}
        fill="#1c2530"
      />

      {/* Lighthouse */}
      <g>
        <rect
          x={X_LIGHTHOUSE - 6}
          y={Y_LIGHTHOUSE_TOP}
          width="12"
          height={Y_MHWS - Y_LIGHTHOUSE_TOP - 30}
          fill="#fafaf9"
          stroke="#0f172a"
          strokeWidth="1"
        />
        {/* Lamp room */}
        <rect
          x={X_LIGHTHOUSE - 9}
          y={Y_LIGHTHOUSE_TOP - 12}
          width="18"
          height="14"
          fill="#fcd34d"
          stroke="#0f172a"
          strokeWidth="1"
        />
        {/* Roof cap */}
        <polygon
          points={`${X_LIGHTHOUSE - 10},${Y_LIGHTHOUSE_TOP - 12} ${X_LIGHTHOUSE + 10},${Y_LIGHTHOUSE_TOP - 12} ${X_LIGHTHOUSE},${Y_LIGHTHOUSE_TOP - 22}`}
          fill="#1c2530"
        />
      </g>

      {/* Drying rock label — peak at (270, Y_DRYING_TOP-5) */}
      {/* (the seabed path already draws it) */}

      {/* Sounding — underwater rock/feature at the seabed under the
          left column. Just a small symbol on the seabed. */}
      <ellipse
        cx={X_SOUNDING}
        cy={Y_SOUNDING_SEABED + 14}
        rx="14"
        ry="5"
        fill="#7d6a44"
      />

      {/* ── Chart annotations + arrows to the datums ─────────── */}

      {/* Sounding 5₄ — below CD */}
      <ChartFigure
        x={X_SOUNDING}
        y={Y_SOUNDING_SEABED - 6}
        anchor="middle"
      >
        <tspan fontWeight="700">5</tspan>
        <tspan fontSize="9" baselineShift="sub" fontWeight="700">
          4
        </tspan>
      </ChartFigure>
      <DatumArrow
        x={X_SOUNDING}
        yFrom={Y_SOUNDING_SEABED - 14}
        yTo={Y_CD + 2}
        stroke="#0c4a6e"
        labelX={X_SOUNDING - 70}
        labelY={(Y_SOUNDING_SEABED + Y_CD) / 2 + 8}
        label="Sounding"
        sub="5.4 m below CD"
        anchor="end"
      />

      {/* Drying height ₂₃ (underlined on chart) — above CD */}
      <ChartFigure
        x={X_DRYING}
        y={Y_DRYING_TOP - 10}
        anchor="middle"
      >
        <tspan fontWeight="700" textDecoration="underline">
          2
        </tspan>
        <tspan
          fontSize="9"
          baselineShift="sub"
          fontWeight="700"
          textDecoration="underline"
        >
          3
        </tspan>
      </ChartFigure>
      <DatumArrow
        x={X_DRYING + 26}
        yFrom={Y_DRYING_TOP - 5}
        yTo={Y_CD}
        stroke="#a16207"
        labelX={X_DRYING + 34}
        labelY={(Y_DRYING_TOP + Y_CD) / 2 + 4}
        label="Drying height"
        sub="2.3 m above CD (underlined)"
        anchor="start"
      />

      {/* Vertical clearance — chart figure sits ABOVE the deck
          (chart-style annotation on the road surface); the arrow +
          descriptive label live INSIDE the bridge arch below.
          Splits them vertically so they no longer collide. */}
      <ChartFigure
        x={X_BRIDGE}
        y={Y_BRIDGE_BOTTOM - 12}
        anchor="middle"
      >
        <tspan fontWeight="700">↥</tspan>
        <tspan fontWeight="700"> 21m</tspan>
      </ChartFigure>
      <DatumArrow
        x={X_BRIDGE - 36}
        yFrom={Y_BRIDGE_BOTTOM + 8}
        yTo={Y_MHWS}
        stroke="#1e6091"
        labelX={X_BRIDGE - 42}
        labelY={Y_MHWS - 16}
        label="Vertical clearance"
        sub="21 m above MHWS"
        anchor="end"
      />

      {/* Light height — chart figure sits BESIDE the lamp room
          (left of the tower) so it doesn't fly off-screen, and the
          descriptive label is parked low on the arrow just above
          MHWS so it doesn't collide with the hilltop label. */}
      <ChartFigure
        x={X_LIGHTHOUSE - 26}
        y={Y_LIGHT - 12}
        anchor="end"
      >
        <tspan fontStyle="italic" fontWeight="700">
          Fl(2) 25m
        </tspan>
      </ChartFigure>
      <DatumArrow
        x={X_LIGHTHOUSE + 18}
        yFrom={Y_LIGHT - 4}
        yTo={Y_MHWS}
        stroke="#b45309"
        labelX={X_LIGHTHOUSE + 24}
        labelY={Y_MHWS - 26}
        label="Light height"
        sub="25 m above MHWS"
        anchor="start"
      />

      {/* Spot height — chart figure above the hilltop, label parked
          well below the chart figure on the long arrow down to
          MHWS so it sits between the light height and MHWS rows. */}
      <ChartFigure
        x={X_HILL}
        y={Y_HILLTOP - 4}
        anchor="middle"
      >
        <tspan fontStyle="italic" fontWeight="700">
          (102)
        </tspan>
      </ChartFigure>
      <DatumArrow
        x={X_HILL + 22}
        yFrom={Y_HILLTOP + 6}
        yTo={Y_MHWS}
        stroke="#7c3aed"
        labelX={X_HILL + 28}
        labelY={(Y_HILLTOP + Y_MHWS) / 2 + 12}
        label="Spot height"
        sub="102 m above MHWS"
        anchor="start"
      />
    </svg>
  );
}

function ChartFigure({
  x,
  y,
  anchor,
  children,
}: {
  x: number;
  y: number;
  anchor: "start" | "middle" | "end";
  children: React.ReactNode;
}) {
  return (
    <text
      x={x}
      y={y}
      fontSize="13"
      textAnchor={anchor}
      fontFamily="ui-sans-serif, system-ui"
      fill="#0f172a"
    >
      {children}
    </text>
  );
}

function DatumArrow({
  x,
  yFrom,
  yTo,
  stroke,
  labelX,
  labelY,
  label,
  sub,
  anchor,
}: {
  x: number;
  yFrom: number;
  yTo: number;
  stroke: string;
  labelX: number;
  labelY: number;
  label: string;
  sub: string;
  anchor: "start" | "end";
}) {
  const head = 3;
  const dy = yTo > yFrom ? 1 : -1;
  return (
    <g stroke={stroke} fill={stroke} fontFamily="ui-sans-serif, system-ui">
      <line x1={x} y1={yFrom} x2={x} y2={yTo} strokeWidth="0.9" />
      <polygon
        points={`${x},${yTo} ${x - head},${yTo - head * dy} ${x + head},${yTo - head * dy}`}
      />
      <text
        x={labelX}
        y={labelY}
        fontSize="10.5"
        textAnchor={anchor}
        stroke="none"
        fontWeight="600"
      >
        {label}
      </text>
      <text
        x={labelX}
        y={labelY + 11}
        fontSize="9"
        textAnchor={anchor}
        stroke="none"
        fill="#5d6878"
        fontWeight="400"
      >
        {sub}
      </text>
    </g>
  );
}
